import React, { Dispatch, SetStateAction } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import {
  BasicDoughnut,
  DoughnutContent,
  Spinner,
  SplitContent,
  SplitContentProps,
} from '@acorns/web-components'
import { isEmpty, isNil } from 'ramda'
import { branch, compose, renderComponent, withHandlers, withState } from 'recompose'
import styled from 'styled-components'

import { Etf, Portfolio } from 'generated/types'
import { CRYPTO_GRADIENT } from 'pages/PortfolioTheme/preview-portfolio/Crypto/useCrypto'
import theme from 'theme'
import { formatPercentage } from 'utils/number/percentage'

type Props = {
  graphData: number[] // Array of decimals < 1. Ex 0.2 corresponds a 20% slice
  header?: React.ReactNode | (() => React.ReactNode)
  onETFPress?: (etf: Etf) => void
  portfolio: Portfolio
  setSelectedEtf: Dispatch<SetStateAction<number>>
  selectedEtf: number
  title?: string
  hasDisplayName?: boolean
  bondEtfPercent?: string
  cryptoEtfPercent?: string
  stockEtfPercent?: string
  graphLabels?: string[]
}

type InnerProps = {
  valueLabel: number
  chartData: number[]
  portfolio: Portfolio
  setValueLabel: (valueLabel?: string) => void
  setSelectedIndex: (selectedIndex: number) => void
} & Props &
  HandlerProps &
  RouteComponentProps<{}>

type HandlerProps = {
  handleSelectedDataChange: (_: any, elements: any) => void
}

type EtfPercentageType = {
  bondEtfPercent?: string
  stockEtfPercent?: string
  cryptoEtfPercent?: string
}

const renderContent = (prop: React.ReactNode | (() => React.ReactNode)) =>
  typeof prop === 'function' ? prop() : prop

const enhance = compose<InnerProps, Props>(
  withRouter,
  branch<InnerProps>(
    // If gql query still loading, display loading spinner
    ({ portfolio }) => isNil(portfolio) || isEmpty(portfolio),
    renderComponent(Spinner),
  ),
  withState('selectedIndex', 'setSelectedIndex', null),
  withHandlers<InnerProps, HandlerProps>({
    handlePageIndicatorClick: ({ setSelectedIndex }) => index => {
      setSelectedIndex(index)
    },
    handlePortfolioChange: () => () => null,
    handleSelectedDataChange: ({ setSelectedEtf }) => (_, elements) => {
      setSelectedEtf(elements[0]?._index ?? null)
    },
  }),
)

const Relative = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 30px;
`

const getDoughnutSplitContent = (
  currentPortfolioIncludesCrypto: boolean,
  EtfPercentage: EtfPercentageType,
): SplitContentProps => {
  if (currentPortfolioIncludesCrypto) {
    return {
      topContent: [
        {
          label: 'Crypto',
          value: EtfPercentage.cryptoEtfPercent,
          bold: true,
          color: theme.colors.purple,
        },
      ],
      bottomContent: [
        {
          label: 'Stocks',
          value: EtfPercentage.stockEtfPercent,
          bold: true,
          color: theme.colors.greenDark,
        },

        {
          label: 'Bonds',
          value: EtfPercentage.bondEtfPercent,
          bold: true,
        },
      ],
    }
  }
  return {
    topContent: [
      {
        label: 'Bonds',
        value: EtfPercentage.bondEtfPercent,
      },
    ],
    bottomContent: [
      {
        label: 'Stocks',
        value: EtfPercentage.stockEtfPercent,
        color: theme.colors.greenDark,
      },
    ],
  }
}

export default enhance(
  ({
    handleSelectedDataChange,
    header,
    portfolio,
    graphData,
    bondEtfPercent,
    stockEtfPercent,
    cryptoEtfPercent,
    graphLabels,
    selectedEtf,
  }) => {
    let currentPortfolioIncludesCrypto = false
    const EtfPercentage = { bondEtfPercent, stockEtfPercent, cryptoEtfPercent }
    const etfTypeColors: Array<string | string[]> = []

    portfolio?.portfolioSecurityAllocations?.forEach(element => {
      if (element?.investmentCategories?.bond === 100) {
        etfTypeColors.push(theme.colors.green)
      } else if (element?.investmentCategories?.crypto === 100) {
        currentPortfolioIncludesCrypto = true
        etfTypeColors.push(CRYPTO_GRADIENT)
      } else {
        etfTypeColors.push(theme.colors.greenDark)
      }
    })

    const { topContent, bottomContent } = getDoughnutSplitContent(
      currentPortfolioIncludesCrypto,
      EtfPercentage,
    )

    return (
      <>
        {header && renderContent(header)}

        <Relative>
          <BasicDoughnut
            data={graphData}
            colors={etfTypeColors}
            onItemHighlight={handleSelectedDataChange}
            options={{
              animation: {
                animateRotate: true,
                animateScale: false,
              },
              round: true,
            }}
          >
            <DoughnutContent
              defaultContent={
                <SplitContent topContent={topContent} bottomContent={bottomContent} />
              }
              selectedIndex={selectedEtf ?? undefined}
              value={formatPercentage(graphData?.[selectedEtf])}
              label={graphLabels?.[selectedEtf]}
            />
          </BasicDoughnut>
        </Relative>
      </>
    )
  },
)
